import { useEffect, useRef, type RefObject } from "react";
import { hasModifierKey, isEditableKeyboardTarget, isHotkeySurfaceActive } from "./hotkeyGuards";

/**
 * 표면 하나의 단축키 목록이다. 리스너·가드 순서·정리는 여기서 소유한다. 소비처는 키와 동작의
 * 대응만 적는다.
 *
 * 리스너는 포커스된 요소가 아니라 `document` 에 등록한다. 포커스를 요구하면 창을 연 직후나
 * 빈 곳을 클릭한 뒤에 키가 동작하지 않는다. 그러면 사용자는 먼저 어딘가를 클릭해야 한다
 * ([hotkeyGuards] 와 같은 판단). 대신 아래 가드로 범위를 줄인다.
 *
 * 창 안 위젯이 자기 키를 먼저 쓰는 경우는 `defaultPrevented` 로 판정한다. 본문은 portal 이고
 * 리스너는 `document` 에 있으므로, 위젯의 React 핸들러가 항상 먼저 실행된다(회귀 테스트:
 * `FullscreenWindow.dom.test.tsx` "본문 위젯이 먼저 소비한 화살표는 셸까지 오지 않는다").
 * 그래서 `[role="listbox"]` 같은 역할을 여기서 열거하지 않는다.
 */
export type WindowHotkeyBinding = {
  /** `KeyboardEvent.key` 값. */
  key: string;
  /**
   * 키를 누르면 실행한다. **없으면 그 키를 처리하지 않는다.** 활성/비활성 축은 따로 없다.
   * 호출부가 조건을 이 값에 반영한다(예: 양 끝에서 멈추는 이동).
   */
  onPress?: () => void;
  /**
   * `"app"`(기본)은 앱 단축키 가드를 모두 적용한다. `"raw"` 는 가드를 건너뛴다. `"raw"` 는 Escape 처럼
   * 입력 중이거나 위에 다른 레이어가 있어도 받아야 하는 키에만 쓴다. 양보 판단은 `onPress` 안에서 한다.
   */
  guard?: "app" | "raw";
};

/**
 * `bindings` 를 `document` keydown 에 등록한다. 렌더마다 다시 구독하지 않도록 최신 바인딩은 ref 로
 * 읽는다. 리스너는 `surfaceRef` 가 바뀔 때만 다시 등록한다.
 */
export function useWindowHotkeys({
  surfaceRef,
  bindings,
}: {
  /** 이 단축키가 속한 표면이다. 언마운트와 `inert`(위에 다른 창/모달) 판정에 쓴다. */
  surfaceRef: RefObject<HTMLElement | null>;
  bindings: readonly WindowHotkeyBinding[];
}): void {
  const bindingsRef = useRef(bindings);
  bindingsRef.current = bindings;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      const binding = bindingsRef.current.find((candidate) => candidate.key === event.key);
      if (binding?.onPress === undefined) {
        return;
      }
      if (binding.guard !== "raw") {
        if (event.defaultPrevented || hasModifierKey(event)) {
          return;
        }
        if (isEditableKeyboardTarget(event.target) || !isHotkeySurfaceActive(surfaceRef.current)) {
          return;
        }
        // 이 키를 실제로 쓸 때만 기본 동작(예: 화살표 스크롤)을 막는다.
        event.preventDefault();
      }
      binding.onPress();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [surfaceRef]);
}
